import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './DownloadPage.css';

const LAUNCHWAY_VERSION = '0.2.42';

// ── Shared primitives ──────────────────────────────────────────────────────

const Screenshot = ({ src, caption }) => (
    <div className="dl-screenshot">
        {src ? (
            <img src={src} alt={caption || 'Screenshot'} className="dl-screenshot-img" />
        ) : (
            <div className="dl-screenshot-inner">
                <span className="dl-screenshot-icon">🖼</span>
                <span className="dl-screenshot-label">Screenshot placeholder</span>
            </div>
        )}
        {caption && <p className="dl-screenshot-caption">{caption}</p>}
    </div>
);

const VideoDemo = ({ src, caption, poster }) => (
    <div className="dl-video-demo">
        <video
            className="dl-video-player"
            controls
            preload="metadata"
            poster={poster}
        >
            <source src={src} type="video/mp4" />
        </video>
        {caption && <p className="dl-screenshot-caption">{caption}</p>}
    </div>
);

const Step = ({ n, title, children }) => (
    <div className="dl-step">
        <div className="dl-step-num">{n}</div>
        <div className="dl-step-body">
            {title && <h4 className="dl-step-title">{title}</h4>}
            <div className="dl-step-content">{children}</div>
        </div>
    </div>
);

const Note = ({ children, type = 'info' }) => (
    <div className={`dl-note dl-note--${type}`}>
        <span className="dl-note-icon">{type === 'warning' ? '⚠' : type === 'tip' ? '💡' : 'ℹ'}</span>
        <span>{children}</span>
    </div>
);

const IC = ({ children }) => <code className="dl-inline-code">{children}</code>;

const CodeBlock = ({ label, code, onCopy, copied }) => (
    <div className="dl-code-block">
        {label && <span className="dl-code-label">{label}</span>}
        <code className="dl-code">{code}</code>
        {onCopy && (
            <button className={`dl-copy-btn ${copied ? 'dl-copy-btn--copied' : ''}`} onClick={onCopy}>
                {copied ? '✓' : 'Copy'}
            </button>
        )}
    </div>
);

const Divider = () => <div className="dl-divider" />;
const Tag  = ({ children }) => <span className="dl-tag">{children}</span>;

const AiOptionsTabs = () => {
    const [active, setActive] = useState('shared');
    return (
        <div className="dl-resume-upload-tabs">
            <div className="dl-upload-tab-bar">
                <button
                    className={`dl-upload-tab ${active === 'shared' ? 'dl-upload-tab--active' : ''}`}
                    onClick={() => setActive('shared')}
                >
                    Launchway shared keys <Tag>Default</Tag>
                </button>
                <button
                    className={`dl-upload-tab ${active === 'own' ? 'dl-upload-tab--active' : ''}`}
                    onClick={() => setActive('own')}
                >
                    Your own Gemini API key <Tag>Recommended</Tag>
                </button>
            </div>
            <div className="dl-upload-tab-panel">
                {active === 'shared' ? (
                    <div className="dl-info-card">
                        <h4>Launchway shared keys <Tag>Default</Tag></h4>
                        <p>Use Launchway&apos;s pooled API quota. No key required. Good for casual or light use.</p>
                    </div>
                ) : (
                    <div className="dl-info-card">
                        <h4>Your own Gemini API key <Tag>Recommended</Tag></h4>
                        <p>Use your own key for dedicated quota. Best for heavy or continuous use. Your key is used <strong>only by Launchway</strong> - it is never sent to third parties or any other service.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const ResumeUploadTabs = () => {
    const [active, setActive] = useState('gdoc');
    return (
        <div className="dl-resume-upload-tabs">
            <Screenshot
                src="/assets/documentation/resume_upload_options.png"
                caption="Choose between Google Doc or PDF resume"
            />
            <div className="dl-upload-tab-bar">
                <button
                    className={`dl-upload-tab ${active === 'gdoc' ? 'dl-upload-tab--active' : ''}`}
                    onClick={() => setActive('gdoc')}
                >
                    Google Doc
                </button>
                <button
                    className={`dl-upload-tab ${active === 'pdf' ? 'dl-upload-tab--active' : ''}`}
                    onClick={() => setActive('pdf')}
                >
                    PDF / DOCX
                </button>
            </div>
            <div className="dl-upload-tab-panel">
                {active === 'gdoc' ? (
                    <Screenshot
                        src="/assets/documentation/resume_gdoc_upload_sucess.png"
                        caption="Google Doc resume linked successfully"
                    />
                ) : (
                    <Screenshot
                        src="/assets/documentation/resume_pdf_upload_success.png"
                        caption="PDF resume uploaded and parsed successfully"
                    />
                )}
            </div>
        </div>
    );
};

// ── Tab content ────────────────────────────────────────────────────────────

const GettingStarted = () => (
    <div className="dl-tab-doc">
        <p className="dl-lead">
            After <strong>installing</strong> (see Installation tab), run <IC>launchway</IC> and follow the prompts.
        </p>

        <Screenshot src="/assets/documentation/launchwayCLI_mainmenu.png" caption="Launchway CLI main menu after login" />

        <h3>First-time setup</h3>
        <ol className="dl-numbered-list">
            <li><strong>Log in</strong> - Create an account or sign in. Session is saved for next time.</li>
            <li><strong>Complete your profile</strong> - Easiest via the Launchway website (link shown in CLI).</li>
            <li><strong>AI provider (optional)</strong> - Pick shared keys, your own Gemini key, or skip and set later in Profile → AI Engine.</li>
            <li><strong>Browser setup</strong> - Menu <Tag>7</Tag>. Run once so the CLI stays logged in to job boards.</li>
        </ol>

        <Note type="tip">
            Your session is saved after first login - next time <IC>launchway</IC> signs you in automatically.
        </Note>
    </div>
);

const OS_TABS = [
    { id: 'windows', label: 'Windows', img: '/assets/windows.png' },
    { id: 'macos',   label: 'macOS',   img: '/assets/apple.png' },
    { id: 'linux',   label: 'Linux',   img: '/assets/linux.png' },
];

const OS_STEPS = {
    windows: {
        checkPython:  'python --version',
        venvCreate:   'python -m venv venv',
        venvActivate: 'venv\\Scripts\\activate',
        install:      'pip install launchway',
        run:          'launchway',
        update:       'pip install --upgrade launchway',
        venvOptionalHint: 'Use a virtual environment if you see permission errors or externally-managed-environment.',
        pathNote: (
            <>
                If <code className="dl-inline-code">launchway</code> is not found, your Python{' '}
                <code className="dl-inline-code">Scripts</code> folder is not on PATH.
                Search <strong>Environment Variables</strong> in Start, then add{' '}
                <code className="dl-inline-code">%APPDATA%\Python\PythonXY\Scripts</code> to your PATH,
                or run <code className="dl-inline-code">python -m launchway</code> as a workaround.
            </>
        ),
    },
    macos: {
        checkPython:  'python3 --version',
        venvCreate:   'python3 -m venv venv',
        venvActivate: 'source venv/bin/activate',
        install:      'pip3 install launchway',
        run:          'launchway',
        update:       'pip3 install --upgrade launchway',
        pathNote: (
            <>
                If <code className="dl-inline-code">launchway</code> is not found, add the pip bin
                directory to your shell profile:{' '}
                <code className="dl-inline-code">
                    echo 'export PATH="$HOME/Library/Python/3.x/bin:$PATH"' {'>>'}  ~/.zshrc && source ~/.zshrc
                </code>
                or run <code className="dl-inline-code">python3 -m launchway</code>.
            </>
        ),
    },
    linux: {
        checkPython:   'python3 --version',
        venvCreate:    'python3 -m venv venv',
        venvActivate:  'source venv/bin/activate',
        install:       'pip install launchway',
        run:           'launchway',
        update:        'pip install --upgrade launchway',
        venvRequired:  true,
        introNote: (
            <>
                On many Linux distros (Ubuntu, Debian, etc.) the system Python is <strong>externally managed</strong> (PEP 668).
                Create a virtual environment first, then install inside it.
            </>
        ),
        pathNote: (
            <>
                If <code className="dl-inline-code">launchway</code> is not found after activating the venv, run{' '}
                <code className="dl-inline-code">./venv/bin/launchway</code> or add <code className="dl-inline-code">venv/bin</code> to your PATH.
            </>
        ),
    },
};

const Installing = ({ copyCode, copied }) => {
    const [os, setOs] = useState('windows');
    const s = OS_STEPS[os];
    const isLinux = os === 'linux';

    return (
        <div className="dl-tab-doc">
            <p className="dl-lead">
                {isLinux
                    ? 'On Linux, use a virtual environment first (system Python is externally managed). On Windows and macOS, a single pip command is enough.'
                    : 'Run the commands below in order. Python 3.11+ required.'}
            </p>

            {s.introNote && <Note type="warning">{s.introNote}</Note>}

            <h3>Commands by operating system</h3>
            <div className="dl-os-container">
                <div className="dl-os-tabs">
                    {OS_TABS.map(t => (
                        <button
                            key={t.id}
                            className={`dl-os-tab ${os === t.id ? 'dl-os-tab--active' : ''}`}
                            onClick={() => setOs(t.id)}
                        >
                            <img src={t.img} alt="" className="dl-os-tab-img" />
                            {t.label}
                        </button>
                    ))}
                </div>
                <div className="dl-install-card dl-install-card--in-os">
                {isLinux ? (
                    <>
                        <CodeBlock label="1" code={s.venvCreate} onCopy={() => copyCode(s.venvCreate, 'venv')} copied={copied === 'venv'} />
                        <CodeBlock label="2" code={s.venvActivate} onCopy={() => copyCode(s.venvActivate, 'act')} copied={copied === 'act'} />
                        <CodeBlock label="3" code={s.install} onCopy={() => copyCode(s.install, 'pip')} copied={copied === 'pip'} />
                        <CodeBlock label="4" code={s.run} onCopy={() => copyCode(s.run, 'run')} copied={copied === 'run'} />
                    </>
                ) : (
                    <>
                        <CodeBlock label="install" code={s.install} onCopy={() => copyCode(s.install, 'pip')} copied={copied === 'pip'} />
                        <CodeBlock label="run" code={s.run} onCopy={() => copyCode(s.run, 'run')} copied={copied === 'run'} />
                    </>
                )}
                </div>
            </div>

            <div className="dl-details">
                <p><strong>Python 3.11+</strong> required - <a className="dl-link" href="https://www.python.org/downloads/" target="_blank" rel="noopener noreferrer">download</a>. {isLinux && <>On Debian/Ubuntu: <IC>sudo apt install python3-venv python3-full</IC> if venv is missing.</>}</p>
                {!isLinux && s.venvOptionalHint && <p><IC>Optional:</IC> {s.venvOptionalHint}</p>}
                <p>First run installs Playwright browser binaries (~200 MB, one-time).</p>
                <Note type="warning">{s.pathNote}</Note>
            </div>

            <h3>Updating</h3>
            <div className="dl-install-card">
                {isLinux && <p className="dl-install-card-hint">Activate your venv first, then:</p>}
                <CodeBlock label="update" code={s.update} onCopy={() => copyCode(s.update, 'upd')} copied={copied === 'upd'} />
            </div>
        </div>
    );
};

const MenuRef = ({ n, label }) => <p className="dl-menu-ref">Menu → <Tag>{n}</Tag> {label}</p>;

const ProfileManagement = () => (
    <div className="dl-tab-doc">
        <MenuRef n={1} label="Profile Management" />
        <p className="dl-lead">
            Your profile fills application forms. The more complete it is, the better. Easiest to edit on the <strong>Launchway website</strong> (link in CLI on first login).
        </p>

        <Screenshot src="/assets/documentation/vew_profile_after_population_from_resume.png" caption="Profile view after being populated from your resume" />

        <h3>Resume upload options</h3>
        <ResumeUploadTabs />

        <h3>Profile sections</h3>
        <div className="dl-table-wrapper">
            <table className="dl-table">
                <thead><tr><th>Section</th><th>Description</th></tr></thead>
                <tbody>
                    <tr><td>Basic Info</td><td>Name, date of birth, gender, nationality, preferred language.</td></tr>
                    <tr><td>Contact Info</td><td>Phone, address, city, state, ZIP, country, LinkedIn, GitHub.</td></tr>
                    <tr><td>Resume</td><td>Your Google Docs resume URL - used for auto-apply and tailoring.</td></tr>
                    <tr><td>Summary / Bio</td><td>Short professional bio used on applications and cover letters.</td></tr>
                    <tr><td>Education</td><td>Degrees, institutions, graduation dates.</td></tr>
                    <tr><td>Work Experience</td><td>Job titles, companies, dates, and descriptions.</td></tr>
                    <tr><td>Projects</td><td>Side projects or portfolio items with descriptions and links.</td></tr>
                    <tr><td>Skills</td><td>Comma-separated technical and soft skills.</td></tr>
                    <tr><td>Cover Letter Template</td><td>A base cover letter that Launchway CLI customises per application.</td></tr>
                    <tr><td>Job Preferences</td><td>Desired roles, locations, salary range, remote preference.</td></tr>
                    <tr><td>EEO / Compliance Info</td><td>Optional equal-opportunity answers required by some applications.</td></tr>
                    <tr><td>Other Links / Portfolio</td><td>Personal website, portfolio, or other relevant links.</td></tr>
                    <tr><td>AI Engine</td><td>Choose Launchway AI (no key needed) or your own Gemini key. Configurable anytime from Settings.</td></tr>
                </tbody>
            </table>
        </div>

        <Note type="info">CLI and website share the same profile - edit in either place.</Note>
    </div>
);

const ResumeTailoring = () => (
    <div className="dl-tab-doc">
        <MenuRef n={2} label="Resume Tailoring" />
        <p className="dl-lead">
            Rewrites your Google Doc resume to highlight keywords for a specific job. Requires: Google Doc URL in profile, AI engine configured.
        </p>

        <h3>Flow</h3>
        <Step n={1} title="Paste the job description">Paste the full job description when prompted.</Step>
        <Step n={2} title="Enter job title and company">Used to name the tailored document and personalise content.</Step>
        <Step n={3} title="Wait 1–2 minutes">Launchway CLI rewrites your resume via AI and uploads the result to Google Docs.</Step>
        <Step n={4} title="Receive the result">Link to the tailored Google Doc is printed; you can open as PDF.</Step>

        <Screenshot src="/assets/documentation/resume_tailoring_paramters_input.png" caption="Resume tailoring parameters - paste job description, job title, and company" />
        <Screenshot src="/assets/documentation/resume_tailored_success.png" caption="Terminal output after successful resume tailoring" />

        <h3>Demo</h3>
        <VideoDemo src="/assets/documentation/resume_tailoring_edited.mp4" caption="Resume tailoring end-to-end demo" />

        <p>Keywords from the job description are woven in; match rate is shown. Your original doc is never modified - a new copy is created each time.</p>
        <Note type="info">Currently only Google Docs resumes. LaTeX support coming later.</Note>
    </div>
);

const JobSearch = () => (
    <div className="dl-tab-doc">
        <MenuRef n={3} label="Search Jobs" />
        <p className="dl-lead">
            Scrapes Indeed, LinkedIn, and others; scores by relevance; apply from the results.
        </p>

        <Screenshot src="/assets/documentation/job_search_demo.png" caption="Job search results with relevance scores" />

        <h3>Parameters</h3>
        <div className="dl-table-wrapper">
            <table className="dl-table">
                <thead><tr><th>Parameter</th><th>Description</th></tr></thead>
                <tbody>
                    <tr><td>Keywords</td><td>E.g. &quot;Software Engineer&quot;, &quot;Data Analyst&quot;. Required.</td></tr>
                    <tr><td>Location</td><td>City, state, or country. Leave blank for any location.</td></tr>
                    <tr><td>Remote only</td><td>Filter to remote-only postings.</td></tr>
                    <tr><td>Easy Apply only</td><td>Restrict to one-click / simplified apply jobs.</td></tr>
                    <tr><td>Recency filter</td><td>Only show jobs posted in the last N hours.</td></tr>
                    <tr><td>Max results</td><td>Default 20.</td></tr>
                </tbody>
            </table>
        </div>

        <p>Results show title, company, location, salary, source, and <strong>relevance score</strong>. At the prompt: <Tag>A</Tag> apply, <Tag>S</Tag> save to JSON, <Tag>Q</Tag> quit.</p>
        <Note type="tip">Let the CLI extract keywords from your resume when first prompted - improves scoring for all future searches.</Note>
    </div>
);

const AutoApply = () => (
    <div className="dl-tab-doc">
        <MenuRef n={4} label="Assisted Auto-Apply (Batch)" />
        <p className="dl-lead">
            Opens job URLs in a browser and fills forms from your profile. Up to 10 per batch. Need: complete profile, AI engine configured, browser setup (menu 7) done once.
        </p>

        <h3>Flow</h3>
        <Step n={1} title="Enter how many jobs">1–10.</Step>
        <Step n={2} title="Paste job URLs">Paste the direct application URL (the page with the form) for each job.</Step>
        <Step n={3} title="Choose headless mode">
            <strong>Headless (y)</strong> - browser is invisible. Faster.<br />
            <strong>Non-headless (n)</strong> - you can watch the browser fill the form. Good for first runs.
        </Step>
        <Step n={4} title="Wait">Progress is printed; results saved to Application History. Incomplete apps get a link to finish manually.</Step>

        <Screenshot src="/assets/documentation/auto_apply_1_job_ss.png" caption="Terminal output during a batch application run" />

        <h3>Demo</h3>
        <VideoDemo src="/assets/documentation/job_application_demo_edited.mp4" caption="Auto apply end-to-end demo - watch the AI fill and submit an application" />

        <Note type="warning">Works best on company career pages and ATS (Greenhouse, Workday, Lever). Heavy anti-bot sites may need non-headless mode.</Note>
    </div>
);

const AppHistory = () => (
    <div className="dl-tab-doc">
        <MenuRef n={5} label="View Application History" />
        <p className="dl-lead">
            Log of applications: company, position, status, date. Last 50 per session.
        </p>

        <Screenshot caption="Application history table in the terminal (coming soon)" />
    </div>
);

const ContinuousMode = () => (
    <div className="dl-tab-doc">
        <MenuRef n={6} label="Fully Autonomous Auto-Apply - Continuous" />
        <p className="dl-lead">
            Fully automated: search → optional tailor → apply, no input. Complete profile and resume URL before starting.
        </p>

        <Note type="info">This mode can submit applications automatically. The agent may still make mistakes, so review your setup before running.</Note>

        <h3>Options</h3>
        <div className="dl-table-wrapper">
            <table className="dl-table">
                <thead><tr><th>Option</th><th>Required / Optional</th><th>What you can set</th><th>Default</th></tr></thead>
                <tbody>
                    <tr><td>Keywords</td><td>Required</td><td>Search terms (e.g. &quot;Software Engineer&quot;, &quot;Data Analyst&quot;).</td><td>-</td></tr>
                    <tr><td>Location</td><td>Optional</td><td>City, state, or country to search in.</td><td>Blank (any location)</td></tr>
                    <tr><td>Remote only</td><td>Optional</td><td>Restrict to remote-only postings.</td><td>Off</td></tr>
                    <tr><td>Easy Apply only</td><td>Optional</td><td>Restrict to one-click / simplified apply jobs.</td><td>Off</td></tr>
                    <tr><td>Recency filter</td><td>Optional</td><td>Only show jobs posted in the last N hours.</td><td>None (all postings)</td></tr>
                    <tr><td>Tailor per job</td><td>Optional</td><td>Tailor your resume for each job before applying.</td><td>Off</td></tr>
                    <tr><td>Proxy support</td><td>Optional</td><td>Proxy URL for requests.</td><td>None</td></tr>
                    <tr><td>Jobs per round</td><td>Optional</td><td>Number of jobs to apply to per round (1–10).</td><td>5</td></tr>
                </tbody>
            </table>
        </div>

        <p>Progress is printed per job; rate limits trigger pause/retry. Progress report (JSON) saved each round. <strong>Stop:</strong> <IC>Ctrl + C</IC> - saves and exits cleanly.</p>

        <Screenshot src="/assets/documentation/auto_apply_continuous_parameters_config_ss.png" caption="Continuous mode configuration parameters" />
        <Screenshot src="/assets/documentation/auto_apply_continuous_demo_ss.png" caption="Continuous mode progress output in the terminal" />

        <Note type="tip">Set recency (e.g. last 24h) to target fresh postings.</Note>
    </div>
);

const BrowserSetup = () => (
    <div className="dl-tab-doc">
        <MenuRef n={7} label="Browser Profile Setup" />
        <p className="dl-lead">
            Persistent Chrome profile so you stay logged in to job boards. <strong>Run once before first auto-apply.</strong> Stored locally only.
        </p>

        <h3>Steps</h3>
        <Step n={1}>Browser opens.</Step>
        <Step n={2}>Log in to LinkedIn, Indeed, Glassdoor, etc.</Step>
        <Step n={3}>Accept cookies / complete verifications.</Step>
        <Step n={4}>Close browser - profile is saved and reused.</Step>

        <Screenshot caption="Browser window opened during profile setup (coming soon)" />

        <p>If a profile exists: continue, reset, or cancel. Profile is local only - not uploaded.</p>
    </div>
);

const Settings = () => (
    <div className="dl-tab-doc">
        <MenuRef n={8} label="Settings" />
        <p className="dl-lead">
            View account info (name, email, verification, applications, daily rate limit usage), change password, update email.
        </p>
    </div>
);

const ApiConfig = () => (
    <div className="dl-tab-doc">
        <p className="dl-lead">
            Gemini AI powers matching, keyword extraction, and form fill. Configure in <Tag>Profile → AI Engine</Tag> on the website or in the CLI.
        </p>

        <h3>Two options</h3>
        <AiOptionsTabs />

        <h3>Primary and secondary methods</h3>
        <p>You can set a <strong>primary</strong> and an optional <strong>secondary</strong> AI method. If the primary fails or hits limits, the CLI can fall back to the secondary. For example: primary = your own key, secondary = Launchway shared keys.</p>

        <h3>Adding your Gemini key</h3>
        <p>Get a key at <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer" className="dl-link">aistudio.google.com/apikey</a>. In the CLI: <Tag>Profile → AI Engine</Tag> → choose &quot;Use my own key&quot; and paste when prompted. If you choose custom API keys, they serve <strong>only Launchway</strong> and are not used outside Launchway.</p>

        <div className="dl-screenshot-row">
            <Screenshot src="/assets/documentation/AI_engine_Setup_primary_launchway_falback_None.png" caption="AI Engine configured: primary = Launchway shared keys, fallback = none" />
            <Screenshot src="/assets/documentation/AI_Engine_Config_warning.png" caption="Warning shown when AI Engine is not yet configured" />
        </div>
    </div>
);

const RateLimits = () => (
    <div className="dl-tab-doc">
        <p className="dl-lead">Check usage in <Tag>Settings → View Account Info</Tag>. Resets midnight UTC.</p>

        <div className="dl-table-wrapper">
            <table className="dl-table">
                <thead><tr><th>Feature</th><th>Beta (free)</th><th>Resets</th></tr></thead>
                <tbody>
                    <tr><td>Resume Tailoring</td><td><strong>5 / day</strong></td><td>Midnight UTC</td></tr>
                    <tr><td>Auto Apply (batch + continuous)</td><td><strong>10 / day</strong></td><td>Midnight UTC</td></tr>
                    <tr><td>Job Search</td><td><strong>20 / day</strong></td><td>Midnight UTC</td></tr>
                    <tr><td>Profile Updates</td><td>Unlimited</td><td>-</td></tr>
                </tbody>
            </table>
        </div>

        <p>Live balance at <IC>launchway.app/manage-credits</IC> and in CLI after each task. Add your own Gemini key for higher limits; upgrade on the website for more. Hit mid-batch? CLI stops cleanly and lists completed vs incomplete.</p>
    </div>
);

const Troubleshooting = () => (
    <div className="dl-tab-doc">
        <p className="dl-lead">Common issues and how to fix them quickly.</p>

        <div className="dl-faq-list">
            <div className="dl-faq-item">
                <h4>"launchway: command not found"</h4>
                <p>Your Python Scripts/bin folder is not on PATH.</p>
                <p><strong>Fix:</strong> Run <IC>python -m launchway</IC>, or add the Scripts/bin directory to your system PATH.</p>
            </div>
            <div className="dl-faq-item">
                <h4>Playwright browser install fails</h4>
                <p><strong>Fix:</strong></p>
                <div className="dl-install-card" style={{marginTop:'0.4rem'}}><CodeBlock code="python -m playwright install chromium" /></div>
            </div>
            <div className="dl-faq-item">
                <h4>Auto-apply fills wrong fields or stops early</h4>
                <p>Run with the browser visible: choose <IC>n</IC> for headless mode when prompted.</p>
                <p>Also run <Tag>7. Browser Profile Setup</Tag> if not done yet - being logged in to job sites dramatically improves success rate.</p>
            </div>
            <div className="dl-faq-item">
                <h4>Resume tailoring fails</h4>
                <p>Check that your Google account is connected and your AI engine is configured. Re-run the tailoring step or reconnect Google via the website.</p>
            </div>
            <div className="dl-faq-item">
                <h4>"AI Engine not configured"</h4>
                <p>Go to <Tag>Profile Management → AI Engine</Tag> and choose a mode (shared keys or your own key).</p>
            </div>
            <div className="dl-faq-item">
                <h4>Session keeps expiring</h4>
                <p>Sessions are stored in <IC>~/.launchway/session.json</IC>. If deleted or expired, log in again. This is expected.</p>
            </div>
        </div>

        <Divider />

        <Note type="tip">
            Run <IC>launchway --version</IC> to confirm your version. Update with <IC>pip install --upgrade launchway</IC>.
        </Note>
    </div>
);

const FAQ = () => (
    <div className="dl-tab-doc">
        <div className="dl-faq-list">
            <div className="dl-faq-item">
                <h4>Does Launchway CLI submit applications without my review?</h4>
                <p>In batch mode (menu item 4) and continuous mode (menu item 6), yes - it submits automatically. Run non-headless to watch it happen. For manual review, just keep the browser visible and don't walk away.</p>
            </div>
            <div className="dl-faq-item">
                <h4>What job sites does it support?</h4>
                <p>Any URL with a standard HTML application form. Works well with Greenhouse, Lever, Workday, iCIMS, SmartRecruiters, and direct company career pages. LinkedIn Easy Apply and Indeed Apply are also supported.</p>
            </div>
            <div className="dl-faq-item">
                <h4>Is my data stored anywhere?</h4>
                <p>Your profile is stored on the Launchway backend (same as the website). Credentials (e.g. Gemini key) are encrypted at rest. Application form data is not retained after submission.</p>
            </div>
            <div className="dl-faq-item">
                <h4>Can I use Launchway CLI on multiple machines?</h4>
                <p>Yes. Profile data is cloud-synced. Install and log in on any machine. Browser Profile Setup needs to be run separately on each device.</p>
            </div>
            <div className="dl-faq-item">
                <h4>Does it work on macOS and Linux?</h4>
                <p>Yes. Tested on Windows, macOS, and Ubuntu. Playwright supports all three.</p>
            </div>
            <div className="dl-faq-item">
                <h4>Can I tailor my resume without auto-applying?</h4>
                <p>Yes. Resume Tailoring (menu item 2) is standalone. It produces a new Google Doc you can review and use however you like.</p>
            </div>
            <div className="dl-faq-item">
                <h4>What happens if an application is incomplete?</h4>
                <p>Launchway CLI reports it clearly after the batch. You can open all incomplete applications in the browser in one click to complete manually - with your login sessions preserved.</p>
            </div>
            <div className="dl-faq-item">
                <h4>How do I stop continuous mode?</h4>
                <p>Press <IC>Ctrl + C</IC>. Launchway CLI catches it, saves a progress report, and exits cleanly.</p>
            </div>
        </div>
    </div>
);

// ── Tab registry (ordered to match the main menu) ─────────────────────────

const TABS = [
    { id: 'installing',       label: 'Installation',            group: 'intro',   Component: Installing },
    { id: 'getting-started',  label: 'Getting Started',         group: 'intro',   Component: GettingStarted },
    { id: 'profile',          label: '1 · Profile Management',  group: 'menu',    Component: ProfileManagement },
    { id: 'tailoring',        label: '2 · Resume Tailoring',    group: 'menu',    Component: ResumeTailoring },
    { id: 'job-search',       label: '3 · Job Search',          group: 'menu',    Component: JobSearch },
    { id: 'auto-apply',       label: '4 · Assisted Auto-Apply', group: 'menu',    Component: AutoApply },
    { id: 'history',          label: '5 · App History',         group: 'menu',    Component: AppHistory },
    { id: 'continuous',       label: '6 · Fully Autonomous',    group: 'menu',    Component: ContinuousMode },
    { id: 'browser-setup',    label: '7 · Browser Setup',       group: 'menu',    Component: BrowserSetup },
    { id: 'settings',         label: '8 · Settings',            group: 'menu',    Component: Settings },
    { id: 'api-config',       label: 'API Configuration',       group: 'ref',     Component: ApiConfig },
    { id: 'rate-limits',      label: 'Rate Limits',             group: 'ref',     Component: RateLimits },
    { id: 'troubleshooting',  label: 'Troubleshooting',         group: 'ref',     Component: Troubleshooting },
    { id: 'faq',              label: 'FAQ',                     group: 'ref',     Component: FAQ },
];

const GROUP_LABELS = { intro: 'Overview', menu: 'Menu Items', ref: 'Reference' };

// ── Prerequisites ─────────────────────────────────────────────────────────

const PREREQUISITES = [
    { name: 'Python 3.11+',    description: 'Required runtime.',              link: 'https://www.python.org/downloads/',                     linkText: 'Download Python', img: '/assets/python.png' },
    { name: 'pip',             description: 'Bundled with Python 3.4+.',      link: 'https://pip.pypa.io/en/stable/installation/',           linkText: 'pip install guide' },
    { name: 'Google Chrome',   description: 'Required for browser automation.', link: 'https://www.google.com/chrome/',                      linkText: 'Download Chrome', img: '/assets/chrome.png' },

];

// ── Page ──────────────────────────────────────────────────────────────────

const DownloadPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('installing');
    const [copied, setCopied]       = useState('');

    const copyCode = (text, key) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(key);
            setTimeout(() => setCopied(''), 2000);
        });
    };

    const { Component } = TABS.find(t => t.id === activeTab);

    // Group tabs for sidebar sections
    const groups = ['intro', 'menu', 'ref'];

    return (
        <div className="dl-container">
            {/* No navbar on this page - keep only Back to Home */}
            <div className="dl-top-actions">
                <button className="back-btn" onClick={() => navigate('/')}>← Back to Home</button>
            </div>

            {/* Hero */}
            <section className="dl-hero">
                    <h1 className="dl-hero-title">Get <span className="dl-accent">Launchway CLI</span> on Your Machine</h1>
                    <p className="dl-hero-subtitle">
                        A command-line tool that automates your job search - AI resume tailoring,
                        multi-site job discovery, and hands-free applications.
                    </p>
                <div className="dl-hero-meta">
                    <span className="dl-meta-chip">
                        <img src="/assets/version.png" alt="" className="dl-meta-chip-img" />
                        v{LAUNCHWAY_VERSION}
                    </span>
                    <span className="dl-meta-chip">
                        <img src="/assets/python.png" alt="" className="dl-meta-chip-img" />
                        Python 3.11+
                    </span>
                    <span className="dl-meta-chip dl-meta-chip--os">
                        <img src="/assets/windows.png" alt="Windows" className="dl-meta-chip-img" />
                        <img src="/assets/apple.png" alt="macOS" className="dl-meta-chip-img" />
                        <img src="/assets/linux.png" alt="Linux" className="dl-meta-chip-img" />
                        Windows · macOS · Linux
                    </span>
                </div>
            </section>

            {/* Quick Install */}
            <section className="dl-section">
                <h2 className="dl-section-title">Quick Install</h2>
                <div className="dl-install-card">
                    <CodeBlock label="install" code="pip install launchway"  onCopy={() => copyCode('pip install launchway', 'pip')} copied={copied === 'pip'} />
                    <CodeBlock label="run"     code="launchway"              onCopy={() => copyCode('launchway', 'run')}             copied={copied === 'run'} />
                    <CodeBlock label="version" code="launchway --version"    onCopy={() => copyCode('launchway --version', 'ver')}   copied={copied === 'ver'} />
                </div>
            </section>

            {/* Prerequisites */}
            <section className="dl-section">
                <h2 className="dl-section-title">Prerequisites</h2>
                <div className="dl-prereq-grid">
                    {PREREQUISITES.map(p => (
                        <div key={p.name} className="dl-prereq-card">
                            <div className="dl-prereq-icon">
                                {p.img ? <img src={p.img} alt="" className="dl-prereq-img" /> : null}
                            </div>
                            <div className="dl-prereq-body">
                                <h3 className="dl-prereq-name">{p.name}</h3>
                                <p className="dl-prereq-desc">{p.description}</p>
                                <a href={p.link} target="_blank" rel="noopener noreferrer" className="dl-prereq-link">{p.linkText} ↗</a>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Docs - sidebar layout */}
            <section className="dl-section">
                <h2 className="dl-section-title">Documentation</h2>
                <div className="dl-docs-layout">

                    {/* Sidebar */}
                    <nav className="dl-sidebar">
                        {groups.map(group => (
                            <div key={group} className="dl-sidebar-group">
                                <div className="dl-sidebar-group-label">{GROUP_LABELS[group]}</div>
                                {TABS.filter(t => t.group === group).map(tab => (
                                    <button
                                        key={tab.id}
                                        className={`dl-sidebar-btn ${activeTab === tab.id ? 'dl-sidebar-btn--active' : ''}`}
                                        onClick={() => setActiveTab(tab.id)}
                                    >
                                        <span className="dl-sidebar-label">{tab.label}</span>
                                    </button>
                                ))}
                            </div>
                        ))}
                    </nav>

                    {/* Content */}
                    <div className="dl-doc-content">
                        <Component copyCode={copyCode} copied={copied} />
                    </div>
                </div>
            </section>

            <div className="dl-footer-note">
                <p>Need help? Reach out via the app or contact us through the website.</p>
            </div>
        </div>
    );
};

export default DownloadPage;
